import { PopupCloseSetting } from '@/constants'
import { useSessionStore, useUIStore } from '@/stores'
import { authService } from '@/services/authService'
import { isPortalStep } from './portalActions'
import { findPortalInput, inputTypeForPath } from './portalNav'
import { returnFunnel } from './portalSession'

export type CloseAction = { kind: 'recover' } | { kind: 'submit'; input: any }

export function resolveCloseAction(step: any): CloseAction {
  const inputs = (step?.valid_inputs as any[]) || []
  const setting = Number(step?.popup_close_setting)
  if (!inputs.length || setting === PopupCloseSetting.NoClose) {
    return { kind: 'recover' }
  }
  const first = { kind: 'submit', input: inputs[0] } as CloseAction
  const last = { kind: 'submit', input: inputs[inputs.length - 1] } as CloseAction

  switch (setting) {
    case PopupCloseSetting.FirstInput:
      return first
    case PopupCloseSetting.LastInput:
      return last
    case PopupCloseSetting.UndoOrFirstInput:
      return step?.can_undo ? { kind: 'recover' } : first
    case PopupCloseSetting.UndoOrLastInput:
      return step?.can_undo ? { kind: 'recover' } : last
    default:
      return { kind: 'recover' }
  }
}

function liveStep(): any {
  const s = useSessionStore.getState()
  return (s.popupSession ?? s.session) as any
}

export function isSyncedToUrl(): boolean {
  const s = useSessionStore.getState()
  if (s.popupSession || !isPortalStep(s.session)) {
    return false
  }
  const target = inputTypeForPath(window.location.pathname)
  return target === undefined || target === Number((s.session as any)?.step_type)
}

let inFlight: Promise<boolean> | null = null

export function resyncPortalSession(): Promise<boolean> {
  if (!inFlight) {
    inFlight = walkBackToPortal().finally(() => {
      inFlight = null
    })
  }
  return inFlight
}

async function walkBackToPortal(): Promise<boolean> {
  const recovered = await attemptRecovery()
  // The single release point. On failure the pin stays: it is the only record of the live
  // funnel step, and clearing it makes every later resync early-return "already synced".
  if (recovered) {
    returnFunnel()
  }
  return recovered
}

async function attemptRecovery(): Promise<boolean> {
  const ui = useUIStore.getState()
  const sessionId = (useSessionStore.getState().session as any)?.id

  if (isSyncedToUrl()) {
    return true
  }
  if (!sessionId) {
    return false
  }

  ui.setForceDisplayLoader(true)
  ui.setLoading(true)
  useSessionStore.getState().setPortalResyncing(true)

  try {
    // Close ladder: one undo, then `home`. Never a walk — repeated undos
    // surface intermediate funnel steps and cost a round trip each.
    const store = useSessionStore.getState()
    if (liveStep()?.can_undo) {
      store.resetUndoCount()
      try {
        await store.undo(1)
      } catch {
        /* home is the fallback */
      }
    }

    if (!isPortalStep(liveStep()) && liveStep()?.can_go_home) {
      try {
        await useSessionStore.getState().sessionAction('home', {})
        useSessionStore.getState().clearStepStack()
      } catch {
        /* handled by the isPortalStep check below */
      }
    }
    useSessionStore.getState().resetUndoCount()

    if (!isPortalStep(liveStep())) {
      return false
    }

    const targetType = inputTypeForPath(window.location.pathname)
    const landed = Number((useSessionStore.getState().session as any)?.step_type)
    if (targetType !== undefined && targetType !== landed) {
      const input = findPortalInput(useSessionStore.getState().session, targetType)
      if (!input) {
        return false
      }
      const result = await authService.input(sessionId, { input: input.id })
      useSessionStore.getState().setSession(result)
    }

    // The walk popped entries it never pushed; a freshly entered step has no history.
    useSessionStore.getState().clearStepStack()
    return true
  } catch {
    return false
  } finally {
    useSessionStore.getState().setPortalResyncing(false)
    ui.setForceDisplayLoader(false)
    ui.setLoading(false)
  }
}
